import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { DetailTypeToPermissionKey } from '@/common/stores/slide-detail.logic';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getV7NdmDeviceMonitoringDeviceInfoByDeviceId } from '@/api-client/ndm/queries/ndm-monitoring-controller/ndm-monitoring-controller';
import type {
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
  SlideDetailAdapterParams,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import { useInfraAdapterBase } from '@/infrastructure/adapter/infraDetail.shared';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { z } from 'zod';

const networkDeviceDetailInputSchema = z
  .object({
    deviceId: z.string().optional(),
    deviceName: z.string().optional(),
    ip: z.string().optional(),
    targetId: z.string().optional(),
    targetName: z.string().optional(),
    selectedTab: z.string().optional(),
    // 인터페이스 탭 초기 필터(전면 패널 포트 선택)로 소비되는 인터페이스명
    interfaceName: z.string().optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
    rawData: z.record(z.any()).optional(),
  })
  .transform(({ deviceId, targetId, deviceName, targetName, ip, rawData, ...rest }) => ({
    ...rest,
    deviceId: deviceId || targetId || rawData?.deviceId || rawData?.targetId || '',
    deviceName: deviceName || targetName || rawData?.deviceName || rawData?.targetName || '',
    ip: ip || rawData?.ip || '',
    fromTime: rest.fromTime ?? rawData?.fromTime,
    toTime: rest.toTime ?? rawData?.toTime,
  }))
  .pipe(
    z.object({
      deviceId: z.string().min(1),
      deviceName: z.string().optional(),
      ip: z.string().optional(),
      selectedTab: z.string().optional(),
      interfaceName: z.string().optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export type NetworkDeviceDetailInput = z.input<typeof networkDeviceDetailInputSchema>;

export const networkDeviceDetailRequiredMap = getRequiredMap(networkDeviceDetailInputSchema);

const getMissingParams = (error: z.ZodError) =>
  error.issues
    .map((issue) => issue.path[0])
    .filter((path): path is string => typeof path === 'string')
    .join(', ');

const fetchNetworkDeviceInfo = async (deviceId: string) => {
  try {
    const { data } = await getV7NdmDeviceMonitoringDeviceInfoByDeviceId(deviceId, {
      frameName: FRAME_NAMES.NETWORK_DEVICE_DETAIL.INFORMATION,
    });
    const deviceInfo = data?.data?.[0];
    return {
      aliasName: deviceInfo?.aliasName ?? '',
      ip: deviceInfo?.ip ?? '',
    };
  } catch {
    return {
      aliasName: '',
      ip: '',
    };
  }
};

const resolveNetworkDeviceInfo = async (params: {
  deviceId: string;
  deviceName?: string;
  ip?: string;
}) => {
  if (params.deviceName && params.ip) {
    return {
      aliasName: params.deviceName,
      ip: params.ip,
    };
  }

  const deviceInfo = await fetchNetworkDeviceInfo(params.deviceId);
  return {
    aliasName: params.deviceName || deviceInfo.aliasName || '',
    ip: params.ip || deviceInfo.ip || '',
  };
};

export const useNetworkDeviceDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const { isSlideDetailOpened, addTabPanel } = useInfraAdapterBase();
  const { addSplitTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();

  const validateNetworkDeviceDetail = async (
    params: Partial<NetworkDeviceDetailInput>,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof networkDeviceDetailInputSchema>;
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        rolePermissionKey:
          rolePermissionKey || DetailTypeToPermissionKey[TOTAL_DETAIL.NETWORK_DEVICE],
        type: TOTAL_DETAIL.NETWORK_DEVICE,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    // 2. 대상 정보 조회 및 필수값 검증
    const parsed = networkDeviceDetailInputSchema.safeParse(params);
    if (checkParams && !parsed.success) {
      if (options.showErrorMessage) {
        showErrorMsg({
          msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
            value: getMissingParams(parsed.error),
          }),
        });
      }
      return { reason: 'invalid_params' };
    }
    if (!parsed.success) {
      return { reason: 'invalid_params' };
    }
    const validResult = parsed.data;

    // 인터페이스 딥링크(targetId = `deviceId|interfaceId` 합성)인 경우에만 INTERFACE 타겟으로 검증한다.
    // selectedTab은 노출 탭(UI)일 뿐이므로 타겟 종류 판별에 사용하지 않는다.
    const isInterface = validResult.deviceId.includes('|');

    // 3. 타겟 유효성 검증
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: validResult.deviceId,
          targetKind: isInterface
            ? TargetStatusRequestTargetKind.INTERFACE
            : TargetStatusRequestTargetKind.NETWORK_DEVICE,
        },
        {
          showMessageIfTargetNil: options.showErrorMessage,
          showErrorMessage: options.showErrorMessage,
        },
      );
      if (!targetValidation.valid) return { reason: targetValidation.reason };
    }

    return {
      reason: 'ok',
      parsedParams: {
        ...validResult,
        deviceId: isInterface ? validResult.deviceId.split('|')[0] : validResult.deviceId,
      },
    };
  };

  const openNetworkDeviceDetail = async ({
    params,
    options,
    selectedTab,
    currentState,
    init,
  }: SlideDetailAdapterParams<
    Partial<NetworkDeviceDetailInput>
  >): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      runTargetValidation = true,
      showErrorMessage = true,
      openMode = 'main',
      keepSplitTab = false,
    } = options ?? {};

    const mergedParams: Partial<NetworkDeviceDetailInput> = {
      ...params,
      selectedTab: selectedTab ?? params.selectedTab,
    };

    const { reason, parsedParams } = await validateNetworkDeviceDetail(mergedParams, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);
    const validResult = parsedParams;

    const deviceInfo = await resolveNetworkDeviceInfo({
      deviceId: validResult.deviceId,
      deviceName: validResult.deviceName,
      ip: validResult.ip,
    });
    const name = deviceInfo.ip
      ? `${deviceInfo.aliasName} | IP : ${deviceInfo.ip}`
      : deviceInfo.aliasName;
    const tab = {
      type: TOTAL_DETAIL.NETWORK_DEVICE,
      name,
      init,
      addition: {
        deviceId: validResult.deviceId,
        networkDeviceName: deviceInfo.aliasName || validResult.deviceName || '',
        fromTime: validResult.fromTime,
        toTime: validResult.toTime,
      },
      state: {
        selectedTab: validResult.selectedTab,
        interfaceName: validResult.interfaceName,
      },
      keepSplitTab: openMode === 'split' ? keepSplitTab : undefined,
    } as const;

    // 4. 탭 패널 열기
    if (openMode === 'split') {
      addSplitTabPanel(tab);
      isSlideDetailOpened.value = true;
      return createSlideDetailResult('ok');
    }

    const result = addTabPanel(tab, currentState);
    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return { isSlideDetailOpened, openNetworkDeviceDetail };
};
